import React from 'react';
import { useTranslation } from 'react-i18next';

interface NmProgressProps {
  value: number; // 0-100
  label?: string;
  variant?: 'primary' | 'secondary' | 'warning' | 'danger';
  showPercent?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

/** Neumorphic inset progress bar. */
export function NmProgress({
  value,
  label,
  variant = 'primary',
  showPercent = false,
  size = 'md',
}: NmProgressProps) {
  const { i18n } = useTranslation();
  const clampedValue = Math.max(0, Math.min(100, value));
  const fillClass = variant !== 'primary' ? `nm-progress-fill--${variant}` : '';
  const height = size === 'sm' ? 4 : size === 'lg' ? 12 : 8;

  return (
    <div className="nm-progress">
      {(label || showPercent) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {label && <span className="nm-input-label">{label}</span>}
          {showPercent && (
            <span
              style={{
                fontSize: 'var(--fs-caption)',
                color: 'var(--fg-3)',
                fontFamily: 'var(--font-mono)',
              }}
            >
              {new Intl.NumberFormat(i18n.resolvedLanguage ?? i18n.language, { style: 'percent', maximumFractionDigits: 0 }).format(clampedValue / 100)}
            </span>
          )}
        </div>
      )}
      <div className="nm-progress-track" style={{ height }}>
        <div
          className={`nm-progress-fill ${fillClass}`}
          style={{ width: `${clampedValue}%` }}
          role="progressbar"
          aria-valuenow={clampedValue}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  );
}
